import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { RatchetAppliedDetails } from '@dealstack/shared/lib/generated/models/RatchetAppliedDetails';
import { RatchetGrowthNoteKind } from '@dealstack/shared/lib/generated/models/RatchetGrowthNoteKind';
import { RatchetShareDifferenceReason } from '@dealstack/shared/lib/generated/models/RatchetShareDifferenceReason';

const metricLabelMap: Record<string, string> = {
	MOIC: 'MOIC',
	XIRR: 'XIRR',
	MOIC_AND_XIRR: 'MOIC & XIRR',
};

const suffixOf = (metricType: string) => (metricType === 'XIRR' ? '%' : 'x');

const DRAWER = 'breakpoint_summary.distribution_rules_drawer';

const sharePartOf = (
	tValuations: TranslateFunction<I18Namespaces.VALUATIONS>,
	ratchet: RatchetAppliedDetails
): string | undefined => {
	if (!ratchet.receivedSharePercentage || !ratchet.shareDifferenceReason) {
		return undefined;
	}
	const received = `${ratchet.receivedSharePercentage}%`;
	const total = `${ratchet.requestedTotalPercentage ?? ''}%`;
	switch (ratchet.shareDifferenceReason) {
		case RatchetShareDifferenceReason.SCALED_BACK:
			return tValuations(`${DRAWER}.ratchet_receives_share_scaled_back`, { received, total });
		case RatchetShareDifferenceReason.SCALED_UP:
			return tValuations(`${DRAWER}.ratchet_receives_share_scaled_up`, { received });
		case RatchetShareDifferenceReason.UNITS_NOT_SHARING:
			return tValuations(`${DRAWER}.ratchet_receives_share_units_not_sharing`, { received });
	}
};

const growthNotePartOf = (
	tValuations: TranslateFunction<I18Namespaces.VALUATIONS>,
	ratchet: RatchetAppliedDetails
): string | undefined => {
	const note = ratchet.growthNote;
	if (!note) {
		return undefined;
	}
	const names = (note.subjectNames ?? []).join(', ');
	const target = {
		names,
		metric: note.metric ? metricLabelMap[note.metric] : '',
		threshold: `${note.threshold ?? ''}${note.metric ? suffixOf(note.metric) : ''}`,
	};
	switch (note.kind) {
		case RatchetGrowthNoteKind.SUBJECT_OWNS_RATCHET:
			return tValuations(`${DRAWER}.ratchet_not_grown_subject_owns`, { names });
		case RatchetGrowthNoteKind.FROZEN_OWN_TARGET:
			return tValuations(`${DRAWER}.ratchet_growth_frozen`, target);
		case RatchetGrowthNoteKind.STOPPED_BY_OTHER_TARGET:
			return tValuations(`${DRAWER}.ratchet_growth_stopped_by_other`, target);
		case RatchetGrowthNoteKind.STOPPED_EARLY:
			if (note.stop === 'overran') {
				return tValuations(`${DRAWER}.ratchet_stopped_early_overran`);
			}
			if (note.stop === 'blocked') {
				return tValuations(`${DRAWER}.ratchet_stopped_early_blocked`);
			}
			return tValuations(`${DRAWER}.ratchet_stopped_early_starved`);
	}
};

export const ratchetAppliedDescription = (
	tValuations: TranslateFunction<I18Namespaces.VALUATIONS>,
	ratchet: RatchetAppliedDetails
): string => {
	const metricLabel = metricLabelMap[ratchet.metricType] ?? ratchet.metricType;
	const metricSuffix = suffixOf(ratchet.metricType);
	const valueText = (value: string, xirrValue: string | null | undefined) =>
		ratchet.metricType === 'MOIC_AND_XIRR' && xirrValue
			? tValuations(
					'breakpoint_summary.distribution_rules_drawer.ratchet_moic_and_xirr_values',
					{
						moic: `${value}x`,
						xirr: `${xirrValue}%`,
					}
				)
			: `${value}${metricSuffix}`;
	const isAtInitial = ratchet.activeHurdleOrder === null;
	const thresholdPart =
		!isAtInitial && ratchet.activeHurdleThreshold
			? ` (${metricLabel} threshold: ${valueText(ratchet.activeHurdleThreshold, ratchet.activeHurdleXirrThreshold)})`
			: '';
	const metricValuePart = ratchet.metricValue
		? `, actual ${metricLabel}: ${valueText(ratchet.metricValue, ratchet.xirrValue)}`
		: '';
	const refusedPart = ratchet.refusedHurdleThreshold
		? tValuations('breakpoint_summary.distribution_rules_drawer.ratchet_tier_refused', {
				metric: metricLabel,
				threshold: valueText(
					ratchet.refusedHurdleThreshold,
					ratchet.refusedHurdleXirrThreshold
				),
			})
		: undefined;
	const partialPart =
		ratchet.paidPartially && ratchet.partialTargetMetric && ratchet.partialTargetThreshold
			? `, ${tValuations(
					'breakpoint_summary.distribution_rules_drawer.ratchet_paid_partially',
					{
						metric: metricLabelMap[ratchet.partialTargetMetric],
						threshold: `${ratchet.partialTargetThreshold}${suffixOf(ratchet.partialTargetMetric)}`,
					}
				)}`
			: '';

	const growthNotePart = growthNotePartOf(tValuations, ratchet);
	const sharePart = sharePartOf(tValuations, ratchet);
	const tail = [sharePart, growthNotePart]
		.filter(Boolean)
		.map((part) => `, ${part}`)
		.join('');

	if (isAtInitial) {
		const reason = growthNotePart ?? refusedPart ?? 'no hurdle met';
		const shareTail = sharePart ? `, ${sharePart}` : '';
		return `${ratchet.securityName}: at initial entitlement ${ratchet.initialEntitlementPercentage}% (${reason}${metricValuePart})${shareTail}`;
	}
	const adjusted = `${ratchet.securityName}: entitlement adjusted from ${ratchet.initialEntitlementPercentage}% to ${ratchet.finalEntitlementPercentage}%${thresholdPart}${metricValuePart}${partialPart}${tail}`;
	return refusedPart ? `${adjusted}, ${refusedPart}` : adjusted;
};
